import { isDefined, makeValuesPredicate } from '@sgme/fp';
import type { BackTestModel, BackTestStatus } from 'state/backTest/backTest.model';
import type { AppState } from 'state/state.model';

export function getBackTestByTabId(appState: AppState, tabId: string): BackTestModel | undefined {
  return appState.backTest[tabId];
}
export function hasBackTestResult(appState: AppState, tabId: string) {
  return getBackTestByTabId(appState, tabId)?.status === 'GET_BACK_TEST_SUCCESS';
}

const isError = makeValuesPredicate(['GET_BACK_TEST_ERROR', 'RUN_BACK_TEST_ERROR'] satisfies BackTestStatus[]);
export function hasBackTestGlobalError(appState: AppState, tabId: string) {
  const backTest = getBackTestByTabId(appState, tabId);
  return isError(backTest?.status) || hasErrorForAllQuotes(appState, tabId);
}
export function hasBackTestPartialError(appState: AppState, tabId: string) {
  const backTest = getBackTestByTabId(appState, tabId);
  return (
    isError(backTest?.status) || hasErrorForInitialQuoteId(appState, tabId) || hasErrorForSecondQuoteId(appState, tabId)
  );
}

export function hasRunBackTest(appState: AppState, tabId: string) {
  return getBackTestByTabId(appState, tabId)?.status !== 'RUN_BACK_TEST_IDLE';
}
const isRunning = makeValuesPredicate([
  'GET_BACK_TEST_LOADING',
  'RUN_BACK_TEST_LOADING',
  'RUN_BACK_TEST_SUCCESS',
  'CANCEL_BACK_TEST',
] satisfies BackTestStatus[]);
export function isBackTestRunning(appState: AppState, tabId: string) {
  const backTest = getBackTestByTabId(appState, tabId);
  return isRunning(backTest?.status);
}

export const getBackTestMaturitiesValidationError = (appState: AppState, tabId: string) => {
  const backTest = getBackTestByTabId(appState, tabId);
  return backTest?.validationErrors.find(e => e.translationKey === 'backTest.errors.invalidMaturitiesRange');
};
export const hasErrorForInitialQuoteId = (appState: AppState, tabId: string) => {
  const backTest = getBackTestByTabId(appState, tabId);
  return hasErrorForQuoteId(appState, tabId, backTest?.quoteIds.initialQuoteId);
};
export const hasErrorForSecondQuoteId = (appState: AppState, tabId: string) => {
  const backTest = getBackTestByTabId(appState, tabId);
  return hasErrorForQuoteId(appState, tabId, backTest?.quoteIds.secondQuoteId);
};
const hasErrorForQuoteId = (appState: AppState, tabId: string, quoteId?: string | null) => {
  const backTest = getBackTestByTabId(appState, tabId);
  return !!quoteId && backTest?.errors?.[quoteId] !== undefined;
};

const hasErrorForAllQuotes = (appState: AppState, tabId: string) => {
  const backTest = getBackTestByTabId(appState, tabId);
  if (backTest === undefined || backTest.errors === undefined) {
    return false;
  }
  return (
    Object.values(backTest?.quoteIds).filter(isDefined).length ===
    Object.values(backTest.errors).filter(isDefined).length
  );
};
